import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ActionButton,
    Divider,
    Flex,
    Item,
    Menu,
    MenuTrigger,
    Picker,
    Section,
    Text,
} from '@adobe/react-spectrum';
import { CardTypeIcon } from '../../common/cardStats/CardTypeIcon';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber } from '../../common/cardStats/formatters';
import { renderManaText } from '../../common/cardStats/manaText';

import './SynergyPane.css';
import './mana-cost.css';

const CATEGORY_ORDER = ['triggers', 'abilities', 'statics', 'replacements'];

const CATEGORY_LABELS = {
    triggers: 'Triggered Abilities',
    abilities: 'Activated Abilities',
    statics: 'Static Abilities',
    replacements: 'Replacement Effects',
};

const CATEGORY_SHORT_LABELS = {
    triggers: 'Triggers',
    abilities: 'Abilities',
    statics: 'Statics',
    replacements: 'Replacements',
};

const CATEGORY_COLORS = {
    triggers: '#cc1a1a',
    abilities: '#e8c600',
    statics: '#4a9bd6',
    replacements: '#5fa85a',
};

/**
 * Soft-deny list shared with the commander synergy pane — these cards /
 * card categories anchor high-scoring entries but don't represent
 * interesting synergy signal (they're just staples). Categories are
 * lowercased at compare time because the backend casing isn't consistent.
 */
const EXCLUDED_ANCHOR_CARD_IDS = new Set(['chrome_mox', 'mox_diamond']);
const EXCLUDED_ANCHOR_CATEGORIES = new Set(['counterspell', 'spotremoval', 'boardwipes']);

function shouldExcludeAnchor(cardId, cardMeta) {
    if (!cardId) return false;
    if (EXCLUDED_ANCHOR_CARD_IDS.has(cardId)) return true;
    const cats = Array.isArray(cardMeta?.categories) ? cardMeta.categories : [];
    return cats.some((c) => EXCLUDED_ANCHOR_CATEGORIES.has((c || '').toLowerCase()));
}

const TYPE_GROUPS = [
    { key: 'commanders', label: 'Commanders', match: (c) => c?.isCommander },
    {
        key: 'planeswalkers',
        label: 'Planeswalkers',
        match: (c) => (c?.types || '').toLowerCase().includes('planeswalker'),
    },
    {
        key: 'creatures',
        label: 'Creatures',
        match: (c) => (c?.types || '').toLowerCase().includes('creature'),
    },
    {
        key: 'artifacts',
        label: 'Artifacts',
        match: (c) => (c?.types || '').toLowerCase().includes('artifact'),
    },
    {
        key: 'enchantments',
        label: 'Enchantments',
        match: (c) => (c?.types || '').toLowerCase().includes('enchantment'),
    },
    {
        key: 'instants',
        label: 'Instants',
        match: (c) => (c?.types || '').toLowerCase().includes('instant'),
    },
    {
        key: 'sorceries',
        label: 'Sorceries',
        match: (c) => (c?.types || '').toLowerCase().includes('sorcery'),
    },
    {
        key: 'battles',
        label: 'Battles',
        match: (c) => (c?.types || '').toLowerCase().includes('battle'),
    },
    {
        key: 'lands',
        label: 'Lands',
        match: (c) => (c?.types || '').toLowerCase().includes('land'),
    },
];

const prettyFormatEffectText = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const categoryColor = (key) => CATEGORY_COLORS[key] || '#8b93a7';

function sourceToCard(source, cards) {
    if (!source) return null;
    const id = typeof source === 'string' ? source : source.id;
    if (!id) return null;
    const card = cards?.[id];
    if (!card) return null;
    const supportText =
        typeof source === 'object' ? source?.supportCondition?.text : null;
    return {
        ...card,
        id,
        extraData: supportText ? `"${supportText}"` : card?.extraData,
    };
}

function extractCardList(rawList, cards) {
    if (!Array.isArray(rawList)) return [];
    const seen = new Set();
    const out = [];
    rawList.forEach((src) => {
        const card = sourceToCard(src, cards);
        if (!card || seen.has(card.id)) return;
        seen.add(card.id);
        out.push(card);
    });
    return out;
}

export function SynergyPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const synergyList = deck?.details?.synergy?.list || {};
    const totalScore = deck?.details?.synergy?.scoring?.totalScore || 0;

    const openCardInfo = useOpenCardInfo();
    const handleSelectCard = (card) => {
        if (!card) return;
        openCardInfo(card);
    };

    const deckStats = useMemo(() => {
        const counts = { triggers: 0, abilities: 0, statics: 0, replacements: 0 };
        const anchorByCard = new Map();
        const allEntries = [];
        let entryCount = 0;
        let cardCount = 0;
        let totalSupporters = 0;
        let totalEnablers = 0;

        Object.keys(synergyList).forEach((cardId) => {
            const node = synergyList[cardId];
            if (!node) return;
            let touched = false;
            CATEGORY_ORDER.forEach((cat) => {
                const bucket = node[cat] || {};
                Object.keys(bucket).forEach((entryKey) => {
                    const entry = bucket[entryKey];
                    if (!entry) return;
                    touched = true;
                    counts[cat] += 1;
                    entryCount += 1;
                    const supporters = entry?.sources || [];
                    const enablers = entry?.enablers || [];
                    totalSupporters += supporters.length;
                    totalEnablers += enablers.length;
                    const score = Number(entry?.conditionScoring?.total || 0);
                    if (!anchorByCard.has(cardId)) {
                        anchorByCard.set(cardId, { cardId, score: 0, entries: 0, supporters: 0 });
                    }
                    const anchor = anchorByCard.get(cardId);
                    anchor.entries += 1;
                    anchor.score += score;
                    anchor.supporters += supporters.length;
                    allEntries.push({
                        cardId,
                        category: cat,
                        entryKey,
                        text: entry?.text,
                        score,
                        supportersCount: supporters.length,
                        enablersCount: enablers.length,
                    });
                });
            });
            if (touched) cardCount += 1;
        });

        const topAnchors = Array.from(anchorByCard.values())
            .filter((a) => !shouldExcludeAnchor(a.cardId, cards?.[a.cardId]))
            .map((a) => {
                const c = cards?.[a.cardId];
                return {
                    ...a,
                    name: c?.name || a.cardId,
                    imageUri: c?.imageUri,
                    types: c?.types,
                };
            })
            .sort((a, b) => b.score - a.score)
            .slice(0, 10);

        const topSynergies = allEntries
            .filter((e) => !shouldExcludeAnchor(e.cardId, cards?.[e.cardId]))
            .sort((a, b) => b.score - a.score)
            .slice(0, 15)
            .map((e) => {
                const c = cards?.[e.cardId];
                return {
                    ...e,
                    name: c?.name || e.cardId,
                    imageUri: c?.imageUri,
                    types: c?.types,
                };
            });

        return {
            counts,
            entryCount,
            cardCount,
            totalSupporters,
            totalEnablers,
            avgEntriesPerCard: cardCount > 0 ? entryCount / cardCount : 0,
            avgSupportersPerEntry: entryCount > 0 ? totalSupporters / entryCount : 0,
            avgEnablersPerEntry: entryCount > 0 ? totalEnablers / entryCount : 0,
            topAnchors,
            topSynergies,
        };
    }, [synergyList, cards]);

    const entryCategoryData = useMemo(() => {
        const total = CATEGORY_ORDER.reduce((sum, k) => sum + (deckStats.counts[k] || 0), 0);
        if (total <= 0) return [];
        return CATEGORY_ORDER.filter((k) => (deckStats.counts[k] || 0) > 0).map((k) => ({
            key: k,
            label: CATEGORY_SHORT_LABELS[k],
            color: categoryColor(k),
            count: deckStats.counts[k],
            share: deckStats.counts[k] / total,
        }));
    }, [deckStats.counts]);

    const cardMenu = useMemo(() => {
        const groups = TYPE_GROUPS.map((g) => ({ ...g, items: [] }));
        Object.keys(synergyList).forEach((id) => {
            const card = cards?.[id];
            if (!card) return;
            const group = groups.find((g) => g.match(card)) || groups[groups.length - 1];
            group.items.push({ id, name: card?.name || id });
        });
        return groups
            .filter((g) => g.items.length > 0)
            .map((g) => ({
                id: g.key,
                name: g.label,
                children: g.items.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
            }));
    }, [synergyList, cards]);

    const firstCardId = cardMenu[0]?.children?.[0]?.id || null;
    const [selectedCardId, setSelectedCardId] = useState(firstCardId);

    useEffect(() => {
        if (!selectedCardId && firstCardId) setSelectedCardId(firstCardId);
    }, [firstCardId, selectedCardId]);

    const effectMenu = useMemo(() => {
        if (!selectedCardId) return [];
        const node = synergyList[selectedCardId] || {};
        return CATEGORY_ORDER.filter((cat) => node[cat] && Object.keys(node[cat]).length > 0).map(
            (cat) => ({
                id: cat,
                name: CATEGORY_LABELS[cat],
                children: Object.keys(node[cat]).map((entryKey) => {
                    const entry = node[cat][entryKey];
                    return {
                        id: `${cat}::${entryKey}`,
                        category: cat,
                        entryKey,
                        name: prettyFormatEffectText(entry?.text) || entryKey,
                        text: entry?.text,
                    };
                }),
            })
        );
    }, [synergyList, selectedCardId]);

    const flatEffects = useMemo(
        () => effectMenu.flatMap((section) => section.children),
        [effectMenu]
    );
    const [selectedEffectId, setSelectedEffectId] = useState(flatEffects[0]?.id || null);

    useEffect(() => {
        if (!flatEffects.length) {
            setSelectedEffectId(null);
            return;
        }
        if (!selectedEffectId || !flatEffects.find((e) => e.id === selectedEffectId)) {
            setSelectedEffectId(flatEffects[0].id);
        }
    }, [flatEffects, selectedEffectId]);

    const selectedEffect = flatEffects.find((e) => e.id === selectedEffectId) || flatEffects[0];
    const rawEntry = selectedEffect
        ? synergyList?.[selectedCardId]?.[selectedEffect.category]?.[selectedEffect.entryKey]
        : null;

    const groupOptions = useMemo(() => {
        if (!rawEntry) return [];
        const defs = [
            { key: 'sources', label: 'Supporters', source: rawEntry?.sources },
            { key: 'enablers', label: 'Enablers', source: rawEntry?.enablers },
            {
                key: 'cardsOfSupportingType',
                label: 'Cards of supporting type',
                source: rawEntry?.cardsOfSupportingType,
            },
            {
                key: 'cardsOfSupportingColor',
                label: 'Cards of supporting color',
                source: rawEntry?.cardsOfSupportingColor,
            },
            { key: 'targets', label: 'Targets', source: rawEntry?.targets },
            { key: 'replacements', label: 'Replacements', source: rawEntry?.replacements },
            {
                key: 'textPartCardsOfSupportingType',
                label: 'Text-part type matches',
                source: rawEntry?.textPartCardsOfSupportingType,
            },
        ];
        return defs
            .map((d) => ({ ...d, cards: extractCardList(d.source, cards) }))
            .filter((d) => d.cards.length > 0)
            .map((d) => ({
                key: d.key,
                label: `${d.label} (${d.cards.length})`,
                cards: d.cards,
            }));
    }, [rawEntry, cards]);

    const [selectedGroup, setSelectedGroup] = useState(groupOptions[0]?.key || null);

    useEffect(() => {
        if (!groupOptions.length) {
            setSelectedGroup(null);
            return;
        }
        if (!groupOptions.find((o) => o.key === selectedGroup)) {
            setSelectedGroup(groupOptions[0].key);
        }
    }, [groupOptions, selectedGroup]);

    const selectedCard = selectedCardId ? cards?.[selectedCardId] : null;
    const currentGroup = groupOptions.find((g) => g.key === selectedGroup) || groupOptions[0];
    const currentCards = currentGroup?.cards || [];

    if (deckStats.entryCount === 0) {
        return (
            <Flex direction="column" UNSAFE_className="synergyPane" padding="12px">
                <Text UNSAFE_className="commanderCardStatsEmpty">
                    No synergy entries found for this deck yet.
                </Text>
            </Flex>
        );
    }

    return (
        <Flex direction="column" UNSAFE_className="synergyPane" rowGap={20} padding="12px">

            <div className="statsMetricsGrid">
                <StatMetricTile
                    label="Synergy score"
                    value={formatInt(totalScore)}
                    hint={`across ${deckStats.cardCount} synergy cards`}
                />
                <StatMetricTile
                    label="Entries"
                    value={formatInt(deckStats.entryCount)}
                    hint={`${formatNumber(deckStats.avgEntriesPerCard, 1)} per card`}
                />
                <StatMetricTile
                    label="Supporters"
                    value={formatInt(deckStats.totalSupporters)}
                    hint={`${formatNumber(deckStats.avgSupportersPerEntry, 1)} per entry`}
                />
                <StatMetricTile
                    label="Enablers"
                    value={formatInt(deckStats.totalEnablers)}
                    hint={`${formatNumber(deckStats.avgEnablersPerEntry, 1)} per entry`}
                />
            </div>

            {entryCategoryData.length > 0 && (
                <StatsSection
                    title="Entry density by category"
                    subtitle="Where synergy entries live — triggered vs activated vs static vs replacement"
                >
                    <div className="synergyStackedBar">
                        {entryCategoryData.map((segment) => (
                            <div
                                key={segment.key}
                                className="synergyStackedSegment"
                                style={{
                                    width: `${Math.max(segment.share * 100, 2)}%`,
                                    background: segment.color,
                                }}
                                title={`${segment.label} — ${segment.count} entries`}
                            />
                        ))}
                    </div>
                    <div className="synergyStackedLegend">
                        {entryCategoryData.map((segment) => (
                            <div key={segment.key} className="synergyStackedLegendItem">
                                <span
                                    className="synergyStackedSwatch"
                                    style={{ background: segment.color }}
                                />
                                <span className="synergyStackedLegendLabel">{segment.label}</span>
                                <span className="synergyStackedLegendValue">{segment.count}</span>
                            </div>
                        ))}
                    </div>
                </StatsSection>
            )}

            <StatsSection
                title="Effect drilldown"
                subtitle="Pick a card and one of its effects to see every supporter + enabler in this deck"
            >
                <Flex
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    rowGap={12}
                    UNSAFE_className="synergyHeaderBlock"
                >
                    {selectedCard?.imageUri && (
                        <img
                            src={selectedCard.imageUri}
                            alt={selectedCard.name}
                            className="deckSynergyCardImage"
                            onClick={() => handleSelectCard(selectedCard)}
                        />
                    )}
                    <Flex direction="row" alignItems="center" columnGap={10} wrap rowGap={8}>
                        <Text UNSAFE_className="synergyMenuBoxLabel">CARD:</Text>
                        <MenuTrigger>
                            <ActionButton UNSAFE_className="synergyEffectButton">
                                {selectedCard?.name || 'Select card'}
                            </ActionButton>
                            <Menu
                                UNSAFE_className="synergyMenuBox"
                                items={cardMenu}
                                selectedKeys={selectedCardId ? [selectedCardId] : []}
                                onSelectionChange={(keys) => {
                                    const next = Array.from(keys)[0];
                                    if (next) setSelectedCardId(next);
                                }}
                                selectionMode="single"
                            >
                                {(section) => (
                                    <Section items={section.children} title={section.name}>
                                        {(item) => <Item key={item.id}>{item.name}</Item>}
                                    </Section>
                                )}
                            </Menu>
                        </MenuTrigger>
                    </Flex>
                    {flatEffects.length > 0 && (
                        <Flex direction="row" alignItems="center" columnGap={10} wrap rowGap={8}>
                            <Text UNSAFE_className="synergyMenuBoxLabel">EFFECT:</Text>
                            <MenuTrigger>
                                <ActionButton UNSAFE_className="synergyEffectButton">
                                    {renderManaText(
                                        prettyFormatEffectText(rawEntry?.text) ||
                                            selectedEffect?.name ||
                                            'Select'
                                    )}
                                </ActionButton>
                                <Menu
                                    UNSAFE_className="synergyMenuBox"
                                    items={effectMenu}
                                    selectedKeys={selectedEffectId ? [selectedEffectId] : []}
                                    onSelectionChange={(keys) => {
                                        const next = Array.from(keys)[0];
                                        if (next) setSelectedEffectId(next);
                                    }}
                                    selectionMode="single"
                                >
                                    {(section) => (
                                        <Section items={section.children} title={section.name}>
                                            {(item) => (
                                                <Item key={item.id} textValue={item.name}>
                                                    <Text>{renderManaText(item.name)}</Text>
                                                </Item>
                                            )}
                                        </Section>
                                    )}
                                </Menu>
                            </MenuTrigger>
                        </Flex>
                    )}
                </Flex>

                <Divider size="S" />

                {groupOptions.length === 0 ? (
                    <Text UNSAFE_className="commanderCardStatsEmpty">
                        No supporting cards recorded for this effect.
                    </Text>
                ) : (
                    <Flex direction="column" rowGap={10}>
                        {groupOptions.length < 2 ? (
                            <Text UNSAFE_className="synergyCardListTitle">
                                {groupOptions[0]?.label}
                            </Text>
                        ) : (
                            <Flex direction="row" alignItems="end">
                                <Picker
                                    aria-label="Card group"
                                    selectedKey={selectedGroup}
                                    onSelectionChange={setSelectedGroup}
                                    items={groupOptions}
                                    width={260}
                                >
                                    {(option) => <Item key={option.key}>{option.label}</Item>}
                                </Picker>
                            </Flex>
                        )}
                        <div className="synergyCardListBlock synergyCardListScroll">
                            <CardStatList
                                cards={currentCards}
                                deckCount={1}
                                onSelectCard={handleSelectCard}
                                resetKey={`deckSynergy:${selectedCardId}:${selectedEffectId}:${selectedGroup}`}
                                initialVisible={60}
                                compactFilters
                                emptyMessageUnfiltered="No cards recorded for this effect."
                                renderCard={(card) => (
                                    <DeckSynergyCardRow
                                        key={card.id}
                                        card={card}
                                        onSelectCard={handleSelectCard}
                                    />
                                )}
                            />
                        </div>
                    </Flex>
                )}
            </StatsSection>

            {deckStats.topSynergies.length > 0 && (
                <StatsSection
                    title="Top synergies"
                    subtitle="Individual effects ranked by score contribution"
                >
                    <Flex direction="column" rowGap={6}>
                        {deckStats.topSynergies.map((synergy) => (
                            <DeckTopSynergyRow
                                key={`${synergy.cardId}::${synergy.category}::${synergy.entryKey}`}
                                synergy={synergy}
                                onSelectCard={handleSelectCard}
                            />
                        ))}
                    </Flex>
                </StatsSection>
            )}

        </Flex>
    );
}

function DeckSynergyAnchorRow({ anchor, onSelectCard }) {
    const handleClick = () => {
        if (anchor?.imageUri) onSelectCard?.({ imageUri: anchor.imageUri, name: anchor.name });
    };
    return (
        <Flex
            direction="row"
            alignItems="center"
            columnGap={10}
            UNSAFE_className="synergyAnchorRow"
        >
            {anchor?.imageUri ? (
                <img
                    src={anchor.imageUri}
                    alt={anchor.name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={anchor?.types} />
            <span
                className="cardStatName"
                onClick={handleClick}
                style={{ cursor: anchor?.imageUri ? 'pointer' : 'default' }}
                title={anchor?.name}
            >
                {anchor?.name}
            </span>
            <div className="synergyAnchorScores">
                <span
                    className="synergyAnchorScore"
                    title="Number of synergy entries on this card"
                >
                    {`${anchor.entries} ent`}
                </span>
                <span
                    className="synergyAnchorScore is-primary"
                    title="Total synergy score contributed by this card"
                >
                    {`${formatInt(anchor.score)} pts`}
                </span>
                <span
                    className="synergyAnchorScore"
                    title="Supporters across all entries on this card"
                >
                    {`${anchor.supporters} sup`}
                </span>
            </div>
        </Flex>
    );
}

function DeckSynergyCardRow({ card, onSelectCard }) {
    const handleClick = () => {
        if (card?.imageUri) onSelectCard?.(card);
    };
    return (
        <Flex
            direction="row"
            alignItems="center"
            columnGap={10}
            UNSAFE_className="synergyCardRow"
        >
            {card?.imageUri ? (
                <img
                    src={card.imageUri}
                    alt={card.name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={card?.types} />
            <Flex direction="column" flex={1} minWidth={0}>
                <span
                    className="synergyCardRowName"
                    onClick={handleClick}
                    style={{ cursor: card?.imageUri ? 'pointer' : 'default' }}
                    title={card?.name}
                >
                    {card?.name}
                </span>
                {card?.extraData && (
                    <span className="synergyCardRowText">
                        {renderManaText(card.extraData)}
                    </span>
                )}
            </Flex>
        </Flex>
    );
}

function DeckTopSynergyRow({ synergy, onSelectCard }) {
    const handleClick = () => {
        if (synergy?.imageUri) onSelectCard?.({ imageUri: synergy.imageUri, name: synergy.name });
    };
    const catColor = categoryColor(synergy.category);
    return (
        <div className="topSynergyRow">
            {synergy?.imageUri ? (
                <img
                    src={synergy.imageUri}
                    alt={synergy.name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <div className="topSynergyBody">
                <div className="topSynergyHeader">
                    <span
                        className="topSynergyCategory"
                        style={{ borderLeftColor: catColor, color: catColor }}
                    >
                        {CATEGORY_SHORT_LABELS[synergy.category] || synergy.category}
                    </span>
                    <span
                        className="topSynergyName"
                        onClick={handleClick}
                        style={{ cursor: synergy?.imageUri ? 'pointer' : 'default' }}
                    >
                        {synergy.name}
                    </span>
                </div>
                {synergy?.text && (
                    <span className="topSynergyText">
                        &ldquo;{renderManaText(prettyFormatEffectText(synergy.text))}&rdquo;
                    </span>
                )}
            </div>
            <div className="topSynergyMeta">
                <span
                    className="topSynergyScore"
                    title="Synergy score contribution for this effect"
                >
                    {formatInt(synergy.score)}
                </span>
                <span
                    className="topSynergyCount"
                    title="Supporters / enablers on this effect"
                >
                    {`${synergy.supportersCount}s / ${synergy.enablersCount}e`}
                </span>
            </div>
        </div>
    );
}
